/**
 * createOnlyMineFilter — фабрика фильтра «Мои / Все» для RepositoryTable.
 *
 * Все четыре модуля (Tasks, Leads, Deals, Partners) используют один и тот же
 * паттерн: Button.Group с двумя кнопками + переключение применённого фильтра
 * по user.id. Эта фабрика инкапсулирует UI, state, URL и default ('mine');
 * consumer передаёт только тип репозитория и метод применения «Мои».
 *
 * URL хранит явный sentinel 'mine'/'all': при возврате на вкладку повторно
 * не срабатывает defaultValue 'mine'.
 *
 * @example
 * ```ts
 * export const FilterByOnlyMine = createOnlyMineFilter<LeadRepository>({
 *   key: 'onlyMine',
 *   applyMine: (repo, userId) => repo.filterByManager(userId),
 * });
 * ```
 */
import { useEffect, useState } from 'react';
import { Button } from '@mantine/core';
import logger from '@ayocore/platform/common/helpers/logger';
import { useRepositoryFilter } from '@ayocore/platform/client/components/RepositoryTable/useRepositoryFilter';
import { useAuthController } from '@ayocore/platform/identity/client/useAuthController';

type OnlyMineValue = 'mine' | 'all';

export interface CreateOnlyMineFilterOptions<TRepo> {
  /** Ключ фильтра (URL и controller). По умолчанию 'onlyMine'. */
  key?: string;
  /** Колбек применения «Мои» к репозиторию по текущему userId. */
  applyMine: (repo: TRepo, userId: number) => TRepo;
  /** Метка для логов (имя модуля). */
  logLabel?: string;
}

export function createOnlyMineFilter<TRepo>(opts: CreateOnlyMineFilterOptions<TRepo>) {
  const key = opts.key ?? 'onlyMine';
  const logLabel = opts.logLabel ?? 'FilterByOnlyMine';

  return useRepositoryFilter<TRepo>(
    key,
    ({ filter, disabled, fromUrl, toUrl }) => {
      const [value, setValue] = useState<OnlyMineValue | null>(null);
      const userId = useAuthController(s => s.user?.id);

      useEffect(() => {
        try {
          const raw = fromUrl();
          if (raw === 'mine' && userId) {
            setValue('mine');
            filter(repo => opts.applyMine(repo, userId));
          } else if (raw === 'all') {
            setValue('all');
          }
        } catch (error) {
          logger.warn(`${logLabel}: fromUrl failed`, error);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [userId]);

      const handleClick = (next: OnlyMineValue) => {
        if (next === value) return; // single-select: повторный клик игнорируем
        setValue(next);

        if (next === 'mine' && userId) {
          filter(repo => opts.applyMine(repo, userId));
          toUrl('mine');
        } else {
          filter(null);
          toUrl('all');
        }
      };

      return (
        <Button.Group>
          <Button
            size='xs'
            variant={value === 'mine' ? 'filled' : 'default'}
            onClick={() => handleClick('mine')}
            disabled={disabled || !userId}
          >
            Мои
          </Button>
          <Button
            size='xs'
            variant={value === 'all' ? 'filled' : 'default'}
            onClick={() => handleClick('all')}
            disabled={disabled || !userId}
          >
            Все
          </Button>
        </Button.Group>
      );
    },
    { defaultValue: 'mine' }
  );
}
